'use client';

import { FC, useState } from 'react';
import { Box, Divider, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormTextField, FormDatePicker } from '@/components/common/FormComponents';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormLabel } from '@/components/common/FormLabel';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { TheoryActivityDto, VisibilityDto, ActivityCategoryDto, CreateTheoryActivityRequest } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createTheoryActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';
import { useAuthStore } from '@/stores/authStore';
import { getProblemDetail } from '@/lib/utils/apiErrors';

interface AddTheoryActivityDialogProps {
  open: boolean;
  onClose: () => void;
  dataPackageId: number;
  onSubmit?: (activity: TheoryActivityDto) => void;
}

export const AddTheoryActivityDialog: FC<AddTheoryActivityDialogProps> = ({
  open,
  onClose,
  dataPackageId,
  onSubmit,
}) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();
  const currentUserId = useAuthStore((state) => state.currentUser?.id);

  // Form state
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [activityName, setActivityName] = useState('');
  const [method, setMethod] = useState('');
  const [runtime, setRuntime] = useState('');
  const [software, setSoftware] = useState('');
  const [version, setVersion] = useState('');
  const [link, setLink] = useState('');
  const [visibility, setVisibility] = useState(VisibilityDto.Internal);
  const [description, setDescription] = useState('');
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  // Error states
  const [activityNameError, setActivityNameError] = useState(false);
  const [methodError, setMethodError] = useState(false);

  // Reset form to defaults when dialog closes
  const handleClose = () => {
    setSelectedDate(new Date());
    setActivityName('');
    setMethod('');
    setRuntime('');
    setSoftware('');
    setVersion('');
    setLink('');
    setVisibility(VisibilityDto.Internal);
    setDescription('');
    setActivityNameError(false);
    setMethodError(false);
    onClose();
  };

  const handleSubmit = async () => {
    const saveValidation = validateRequiredToSave(
      createTheoryActivitySaveValidation({
        desc: activityName,
        simulationTechnique: method,
        userId: currentUserId,
        date: selectedDate ?? undefined,
      })
    );
    setActivityNameError(!!saveValidation.fieldErrors.desc);
    setMethodError(!!saveValidation.fieldErrors.simulationTechnique);

    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    try {
      const payload: CreateTheoryActivityRequest = {
        dataPackageId,
        desc: activityName.trim(),
        simulationTechnique: method.trim(),
        runtime: runtime ? Number(runtime) : undefined,
        software: software.trim() || undefined,
        version: version.trim() || undefined,
        link: link.trim() || undefined,
        visibility: visibility,
        date: selectedDate ?? new Date(),
        category: ActivityCategoryDto.Theory,
      };

      const savedActivity = await backend.theoryActivity.create(payload);
      log.debug('[AddTheoryActivityDialog] Saved theory activity:', savedActivity);

      onSubmit?.(savedActivity);
      showSnackbar('Theory activity created successfully.', 'success');
      handleClose();
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to create theory activity.', 'error');
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Add Theory Activity"
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Add Theory Activity"
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      {/* Date */}
      <Box display="flex" gap={2}>
        <Box flex={1}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormDatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                markAsInteracted();
              }}
              required
            />
          </LocalizationProvider>
        </Box>
      </Box>

      <Divider />

      {/* Activity Name */}
      <FormTextField
        label="Activity Name"
        required
        value={activityName}
        onChange={(e) => {
          setActivityName(e.target.value);
          markAsInteracted();
        }}
        size="small"
        error={activityNameError}
        fullWidth
      />

      {/* Method */}
      <FormTextField
        label="Method"
        required
        value={method}
        onChange={(e) => {
          setMethod(e.target.value);
          markAsInteracted();
        }}
        size="small"
        error={methodError}
        fullWidth
      />

      <Divider />

      {/* Runtime and Software */}
      <Box display="flex" gap={2}>
        <FormTextField
          label="Runtime (CPU hours)"
          value={runtime}
          onChange={(e) => {
            setRuntime(e.target.value);
            markAsInteracted();
          }}
          size="small"
          type="number"
          fullWidth
          helperText="Optional: CPU hours used for this activity"
        />
        <FormTextField
          label="Software"
          value={software}
          onChange={(e) => {
            setSoftware(e.target.value);
            markAsInteracted();
          }}
          size="small"
          fullWidth
          helperText="Optional: Software used for this activity"
        />
      </Box>

      {/* Version and Link */}
      <Box display="flex" gap={2}>
        <FormTextField
          label="Version"
          value={version}
          onChange={(e) => {
            setVersion(e.target.value);
            markAsInteracted();
          }}
          size="small"
          fullWidth
          helperText="Optional: Software version"
        />
        <FormTextField
          label="Link"
          value={link}
          onChange={(e) => {
            setLink(e.target.value);
            markAsInteracted();
          }}
          size="small"
          fullWidth
          helperText="Optional: Link to related resources"
        />
      </Box>

      {/* Visibility */}
      <FormControl fullWidth size="small">
        <InputLabel>Visibility</InputLabel>
        <Select
          value={visibility}
          onChange={(e) => {
            setVisibility(e.target.value as VisibilityDto);
            markAsInteracted();
          }}
          label="Visibility"
        >
          <MenuItem value="I">Internal</MenuItem>
          <MenuItem value="U">User</MenuItem>
          <MenuItem value="P">Public</MenuItem>
        </Select>
      </FormControl>

      <Divider />

      {/* Description */}
      <Box width="100%">
        <Typography variant="subtitle2" gutterBottom>
          <FormLabel optional>Description</FormLabel>
        </Typography>
        <Box width="100%">
          <RichTextEditor
            value={description}
            onChange={(value) => {
              setDescription(value);
              markAsInteracted();
            }}
          />
        </Box>
      </Box>
    </StandardDialog>
  );
};
